import { DOCUMENT } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { filter, fromEvent, interval, merge, Observable, Subject, Subscription, take, takeUntil } from "rxjs";
import { parseRealtimeNotification } from "../../../models/api/notification/notification.parser";
import {
  isRealtimeNotification,
  NotificationWebSocketMessage,
  USER_DEVICE_REGISTRATION_REQUIRED_CLOSE_CODE,
} from "../../../models/ws/realtimeNotification";
import { UserSettingsService } from "../../../services/api/user-settings/user-settings.service";
import { NotificationsStateService } from "../../../services/state/notifications-state/notifications-state.service";
import {
  NotificationSubscriptionHandle,
  NotificationsWebsocketService,
} from "../../../services/websocket/notifications-websocket/notifications-websocket.service";
import { WebSocketConnectionClosedError } from "../../../services/websocket/websocket-connection/websocket-connection.service";

@Injectable()
export class NotificationsFacadeService {
  private document = inject(DOCUMENT);
  private userSettings = inject(UserSettingsService);
  private notificationsState = inject(NotificationsStateService);
  private notificationsWebsocket = inject(NotificationsWebsocketService);
  private readonly destroy$ = new Subject<void>();

  private notificationsSubscription?: NotificationSubscriptionHandle;
  private deviceSynchronizationSubscription?: Subscription;
  private currentDeviceId?: string;
  private deviceIdPendingReplacement?: string;
  private deviceRegistrationRetryTimeoutId?: ReturnType<typeof setTimeout>;
  private deviceRegistrationRetryAttempts = 0;
  private realtimeReconnectTimeoutId?: ReturnType<typeof setTimeout>;
  private realtimeReconnectAttempts = 0;
  private shouldBeConnected = false;
  private isDestroyed = false;

  private readonly MAX_FAST_RETRY_ATTEMPTS = 5;
  private readonly BASE_DEVICE_REGISTRATION_RETRY_DELAY_MS = 5000;
  private readonly MAX_DEVICE_REGISTRATION_RETRY_DELAY_MS = 60_000;
  private readonly BASE_REALTIME_RECONNECT_DELAY_MS = 1000;
  private readonly MAX_REALTIME_RECONNECT_DELAY_MS = 15_000;
  private readonly MIN_RETRY_JITTER_FACTOR = 0.8;
  private readonly REALTIME_CONNECTION_CHECK_INTERVAL_MS = 12 * 60 * 60 * 1000;

  public start(): void {
    if (this.shouldBeConnected || this.isDestroyed) {
      return;
    }

    this.shouldBeConnected = true;
    this.deviceRegistrationRetryAttempts = 0;
    this.realtimeReconnectAttempts = 0;
    this.registerLifecycleConnectionChecks();

    const currentDeviceId = this.userSettings.getCurrentDeviceId();
    if (currentDeviceId) {
      this.subscribeToNotifications(currentDeviceId);
    } else {
      this.synchronizeDeviceRegistration(null);
    }
  }

  public destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.isDestroyed = true;
    this.shouldBeConnected = false;
    this.clearDeviceRegistrationRetryTimeout();
    this.clearRealtimeReconnectTimeout();
    this.disconnectRealtimeNotifications();
    this.destroy$.next();
    this.destroy$.complete();
    this.deviceSynchronizationSubscription = undefined;
    this.deviceIdPendingReplacement = undefined;
    this.deviceRegistrationRetryAttempts = 0;
    this.realtimeReconnectAttempts = 0;
  }

  private synchronizeDeviceRegistration(attemptedDeviceId: string | null): void {
    if (!this.shouldBeConnected || this.deviceSynchronizationSubscription) {
      return;
    }

    this.clearDeviceRegistrationRetryTimeout();
    const operation = attemptedDeviceId
      ? this.userSettings.recoverCurrentDeviceRegistration(attemptedDeviceId)
      : this.userSettings.ensureCurrentDeviceRegistered();
    const synchronizationSubscription = operation.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (deviceId: string) => {
        this.deviceRegistrationRetryAttempts = 0;
        if (this.deviceIdPendingReplacement && deviceId !== this.deviceIdPendingReplacement) {
          this.deviceIdPendingReplacement = undefined;
        }
        if (deviceId !== attemptedDeviceId) {
          this.subscribeToNotifications(deviceId);
        }
      },
      error: () => this.handleDeviceSynchronizationFailure(),
    });

    this.deviceSynchronizationSubscription = synchronizationSubscription;
    synchronizationSubscription.add(() => {
      if (this.deviceSynchronizationSubscription === synchronizationSubscription) {
        this.deviceSynchronizationSubscription = undefined;
      }
    });
  }

  private handleDeviceSynchronizationFailure(): void {
    if (!this.shouldBeConnected) {
      return;
    }

    const currentDeviceId = this.userSettings.getCurrentDeviceId();
    if (currentDeviceId && currentDeviceId !== this.deviceIdPendingReplacement) {
      this.deviceIdPendingReplacement = undefined;
      this.subscribeToNotifications(currentDeviceId);
      return;
    }

    this.scheduleDeviceRegistrationRetry();
  }

  private subscribeToNotifications(deviceId: string): void {
    if (!this.shouldBeConnected || this.deviceIdPendingReplacement === deviceId) {
      return;
    }

    if (this.notificationsSubscription && this.currentDeviceId === deviceId) {
      return;
    }
    if (this.notificationsSubscription) {
      const subscription = this.notificationsSubscription;
      subscription.replace({ deviceId });
      this.currentDeviceId = deviceId;
      return;
    }

    this.clearRealtimeReconnectTimeout();
    const subscription = this.notificationsWebsocket.register({ deviceId });
    this.notificationsSubscription = subscription;
    this.currentDeviceId = deviceId;

    subscription.messages$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (message) => this.handleRealtimeMessage(message),
      error: (error: unknown) => this.handleTerminalRealtimeSubscription(subscription, error),
      complete: () => this.handleTerminalRealtimeSubscription(subscription),
    });
  }

  private handleRealtimeMessage(message: NotificationWebSocketMessage): void {
    if (!isRealtimeNotification(message)) {
      return;
    }

    this.realtimeReconnectAttempts = 0;
    this.notificationsState.addNotificationsRealtime(parseRealtimeNotification(message));
  }

  private handleTerminalRealtimeSubscription(subscription: NotificationSubscriptionHandle, error?: unknown): void {
    if (this.notificationsSubscription !== subscription) {
      return;
    }

    const rejectedDeviceId =
      error instanceof WebSocketConnectionClosedError && error.code === USER_DEVICE_REGISTRATION_REQUIRED_CLOSE_CODE
        ? this.currentDeviceId
        : undefined;
    this.clearCurrentConnectionState();
    if (rejectedDeviceId) {
      this.deviceIdPendingReplacement = rejectedDeviceId;
      this.synchronizeDeviceRegistration(rejectedDeviceId);
      return;
    }
    this.scheduleRealtimeReconnect();
  }

  private registerLifecycleConnectionChecks(): void {
    const connectionCheckTriggers: Observable<unknown>[] = [
      interval(this.REALTIME_CONNECTION_CHECK_INTERVAL_MS),
      fromEvent(this.document, "visibilitychange"),
    ];
    if (this.document.defaultView) {
      connectionCheckTriggers.push(fromEvent(this.document.defaultView, "online"));
    }

    merge(...connectionCheckTriggers)
      .pipe(
        filter(() => this.document.visibilityState === "visible"),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.handleVisibleConnectionTrigger());
  }

  private handleVisibleConnectionTrigger(): void {
    if (!this.shouldBeConnected) {
      return;
    }

    const currentDeviceId = this.userSettings.getCurrentDeviceId();
    if (currentDeviceId) {
      if (currentDeviceId === this.deviceIdPendingReplacement) {
        this.synchronizeDeviceRegistration(currentDeviceId);
      } else {
        this.subscribeToNotifications(currentDeviceId);
      }
    } else {
      this.synchronizeDeviceRegistration(null);
    }
  }

  private disconnectRealtimeNotifications(): void {
    const subscription = this.notificationsSubscription;
    this.clearCurrentConnectionState();
    subscription?.close();
  }

  private scheduleDeviceRegistrationRetry(): void {
    if (
      this.deviceRegistrationRetryTimeoutId ||
      !this.shouldBeConnected ||
      this.document.visibilityState !== "visible" ||
      !this.document.defaultView ||
      this.deviceRegistrationRetryAttempts >= this.MAX_FAST_RETRY_ATTEMPTS
    ) {
      return;
    }

    const retryDelay = this.calculateRetryDelay(
      this.BASE_DEVICE_REGISTRATION_RETRY_DELAY_MS,
      this.MAX_DEVICE_REGISTRATION_RETRY_DELAY_MS,
      this.deviceRegistrationRetryAttempts
    );
    this.deviceRegistrationRetryAttempts += 1;

    this.deviceRegistrationRetryTimeoutId = this.document.defaultView.setTimeout(() => {
      this.deviceRegistrationRetryTimeoutId = undefined;
      if (!this.shouldBeConnected || this.document.visibilityState !== "visible") {
        return;
      }

      const currentDeviceId = this.userSettings.getCurrentDeviceId();
      if (currentDeviceId) {
        if (currentDeviceId === this.deviceIdPendingReplacement) {
          this.synchronizeDeviceRegistration(currentDeviceId);
        } else {
          this.subscribeToNotifications(currentDeviceId);
        }
        return;
      }

      this.synchronizeDeviceRegistration(null);
    }, retryDelay);
  }

  private scheduleRealtimeReconnect(): void {
    if (
      this.realtimeReconnectTimeoutId ||
      !this.shouldBeConnected ||
      !this.document.defaultView ||
      this.realtimeReconnectAttempts >= this.MAX_FAST_RETRY_ATTEMPTS
    ) {
      return;
    }

    const retryDelay = this.calculateRetryDelay(
      this.BASE_REALTIME_RECONNECT_DELAY_MS,
      this.MAX_REALTIME_RECONNECT_DELAY_MS,
      this.realtimeReconnectAttempts
    );
    this.realtimeReconnectAttempts += 1;

    this.realtimeReconnectTimeoutId = this.document.defaultView.setTimeout(() => {
      this.realtimeReconnectTimeoutId = undefined;
      if (!this.shouldBeConnected || this.notificationsSubscription) {
        return;
      }

      const currentDeviceId = this.userSettings.getCurrentDeviceId();
      if (currentDeviceId && currentDeviceId !== this.deviceIdPendingReplacement) {
        this.subscribeToNotifications(currentDeviceId);
      }
    }, retryDelay);
  }

  private calculateRetryDelay(baseDelay: number, maximumDelay: number, attempt: number): number {
    const cappedDelay = Math.min(baseDelay * 2 ** attempt, maximumDelay);
    const jitterFactor = this.MIN_RETRY_JITTER_FACTOR + (1 - this.MIN_RETRY_JITTER_FACTOR) * Math.random();
    return Math.round(cappedDelay * jitterFactor);
  }

  private clearDeviceRegistrationRetryTimeout(): void {
    if (this.deviceRegistrationRetryTimeoutId !== undefined) {
      this.document.defaultView?.clearTimeout(this.deviceRegistrationRetryTimeoutId);
      this.deviceRegistrationRetryTimeoutId = undefined;
    }
  }

  private clearRealtimeReconnectTimeout(): void {
    if (this.realtimeReconnectTimeoutId !== undefined) {
      this.document.defaultView?.clearTimeout(this.realtimeReconnectTimeoutId);
      this.realtimeReconnectTimeoutId = undefined;
    }
  }

  private clearCurrentConnectionState(): void {
    this.notificationsSubscription = undefined;
    this.currentDeviceId = undefined;
  }
}
